import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { discoverCollectionFields } from "../services/conditionQueryService";
import { findDuplicates, resolvePresetField } from "../services/duplicateFinderService";
import { exportMatchingJson } from "../services/exportMatchingService";
import { bulkDeleteDocuments } from "../services/firestoreDataService";

const PRESETS = [
  { id: "custom", label: "Custom field" },
  { id: "invoiceNumber", label: "Invoice number" },
  { id: "employeeId", label: "Employee ID" },
  { id: "barcode", label: "Barcode" },
  { id: "deviceName", label: "Device name" },
];

export default function DuplicateFinderPage() {
  const { collectionName, selectCollection, allCollections, user } = useFirestoreAdmin();
  const [fields, setFields] = useState([]);
  const [preset, setPreset] = useState("custom");
  const [field, setField] = useState("");
  const [dupes, setDupes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    discoverCollectionFields(collectionName).then(setFields).catch(() => setFields([]));
  }, [collectionName]);

  const scan = async () => {
    setLoading(true);
    try {
      const f = preset === "custom" ? field : resolvePresetField(preset, fields);
      const rows = await findDuplicates(collectionName, f);
      setDupes(rows);
      toast.success(`${rows.length} duplicate group(s)`);
    } catch (e) {
      toast.error(e?.message || "Scan failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteExtras = async (group) => {
    const [, ...rest] = group.documents;
    if (!rest.length) return;
    if (window.prompt(`Type DELETE to remove ${rest.length} duplicate(s)`) !== "DELETE") return;
    await bulkDeleteDocuments(
      collectionName,
      rest.map((d) => d.id),
      user?.uid || user?.email
    );
    toast.success(`Deleted ${rest.length}`);
    scan();
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold">Duplicate Finder</h1>
        <p className="text-sm text-slate-600">Find duplicate values by field (samples up to 2000 docs).</p>
      </header>
      <div className="fa-card flex flex-wrap gap-2 p-4">
        <select value={collectionName} onChange={(e) => selectCollection(e.target.value)} className="rounded-lg border px-2 py-1.5 text-sm">
          {allCollections.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={preset} onChange={(e) => setPreset(e.target.value)} className="rounded-lg border px-2 py-1.5 text-sm">
          {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {preset === "custom" && (
          <input list="dup-fields" className="rounded-lg border px-2 py-1.5 text-sm" value={field} onChange={(e) => setField(e.target.value)} placeholder="Field" />
        )}
        <datalist id="dup-fields">{fields.map((f) => <option key={f} value={f} />)}</datalist>
        <button type="button" className="fa-btn fa-btn-primary" onClick={scan} disabled={loading}>{loading ? "Scanning…" : "Find duplicates"}</button>
      </div>
      <div className="space-y-3">
        {dupes.map((g) => (
          <div key={g.value} className="fa-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold">{String(g.value)} <span className="text-slate-500">×{g.count}</span></p>
              <div className="flex gap-2">
                <button type="button" className="fa-btn text-xs" onClick={() => exportMatchingJson(g.documents.map((d) => ({ id: d.id, data: d.data })), { filename: `dup-${g.value}.json` })}>Export</button>
                <button type="button" className="fa-btn fa-btn-danger text-xs" onClick={() => deleteExtras(g)}>Delete duplicates (keep first)</button>
              </div>
            </div>
            <p className="mt-1 font-mono text-xs text-slate-500">{g.documents.map((d) => d.id).join(", ")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
